import { Component, inject, Input, OnInit, SimpleChanges } from '@angular/core';
import { AclService } from '@common-services/util/acl.service';
import { IconHelperService } from '@common-services/util/material/icon-helper.service';
import { UnaryFunction } from '@dice/app/types/dice.types';
import { ButtonContent } from '@dice/app/types/table.types';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'table-column-button',
  templateUrl: './table-column-button.component.html',
  standalone: false,
})
export class TableColumnButtonComponent<T> implements OnInit {
  private iconHelper = inject(IconHelperService);
  private acl = inject(AclService);

  @Input() button: ButtonContent;

  @Input() element: T;

  @Input() onClick: UnaryFunction<T>;

  @Input() show: UnaryFunction<T, boolean>;

  @Input() permission: string;

  @Input() iconName: string | UnaryFunction<T, string>;

  @Input() cssClass: string | string[] | UnaryFunction<T, string> = [];

  @Input() isSdxIcon?: boolean;

  @Input() tooltip?: string | UnaryFunction<T, string>;

  @Input() hidden?: boolean | UnaryFunction<T, boolean>;

  @Input() disabled?: boolean | UnaryFunction<T, boolean>;

  @Input() isHeader?: boolean;

  @Input() type?: string; // unused, fixes console error after angular update

  static readonly sortEnabled = false;

  icons: string[] = [];

  hasPermission: boolean;

  cssClasses: string[] = [];

  readonly disabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly HEADER_CSSCLASS: string = 'header-icon';

  ngOnInit(): void {
    this.updateClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { iconName, permission, hidden, cssClass, element, disabled } = changes;

    if (!!permission && permission.firstChange) {
      this.hasPermission = this.acl.hasPermission(this.permission);
    }

    if (iconName) {
      this.updateIcons();
    }

    if (hidden) {
      this.updateHidden();
    }

    if (disabled) {
      this.updateDisabledState();
    }

    if (cssClass) {
      this.updateClasses();
    }

    if (element) {
      this.updateState();
    }
  }

  private updateState(): void {
    this.updateDisabledState();
  }

  click = ($event: MouseEvent): void => {
    $event.stopImmediatePropagation();
    this.onClick(this.element);
    this.updateClasses();
  };

  /**
   * add the defined classes.
   * It can be either a single string or an array of strings and functions
   */
  private updateClasses(): void {
    this.cssClasses = [];
    if (typeof this.cssClass === 'string') {
      this.cssClasses = [this.cssClass];
    } else if (typeof this.cssClass === 'function') {
      this.cssClasses = [this.cssClass(this.element)];
    } else if (Array.isArray(this.cssClass)) {
      this.cssClasses = this.cssClass;
    }
    if (this.isHeader) {
      this.cssClasses.push(this.HEADER_CSSCLASS);
    }
  }

  getIcon(): string {
    if (typeof this.iconName === 'string') {
      return this.iconName;
    }
    return this.iconName(this.element);
  }

  getTooltip(): string {
    if (typeof this.tooltip === 'string') {
      return this.tooltip;
    }
    if (typeof this.tooltip === 'function') {
      return this.tooltip(this.element);
    }
    return null;
  }

  private updateIcons(): void {
    if (!this.isSdxIcon) {
      if (typeof this.iconName === 'string') {
        if (!this.icons.includes(this.iconName as string)) {
          this.icons.push(this.iconName as string);
          this.iconHelper.register(this.iconName);
        }
      } else {
        const getElementIcon = this.iconName;
        const icon = getElementIcon(this.element);
        if (!this.icons.includes(icon)) {
          this.icons.push(icon);
          this.iconHelper.register(icon);
        }
      }
    }
  }

  private updateHidden(): void {
    if (typeof this.hidden !== 'boolean') {
      const getHidden = this.hidden;
      this.hidden = getHidden(this.element);
    }
  }

  private updateDisabledState(): void {
    const isDisabled = typeof this.disabled === 'function' ? this.disabled(this.element) : !!this.disabled;
    this.disabled$.next(isDisabled);
  }
}
